import type { Node as ProsemirrorNode, Schema } from 'prosemirror-model';
import type { Transaction } from 'prosemirror-state';
import { Plugin, TextSelection } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

import { LinkWrapperRegExps } from '@core/utils-legacy';

import type { LinkAttributes, LinkSchema } from './link-mark';
import { LinkMark } from './link-mark';
import type { EditorMode, RtPMPlugin } from '../../../editor/types';
import type { ProsemirrorAttrs } from '../../../schema/schema';

interface StepChanges {
  oldStart: number;
  oldEnd: number;
  newStart: number;
  newEnd: number;
}

interface TextWithPosition {
  text: string;
  pos: number;
}

interface LinkMatcher {
  regexp: RegExp;
  linkFunction: (schema: Schema, text: string) => ProsemirrorNode;
}

export class AutoLinkWrapPlugin implements RtPMPlugin {
  private editorView: EditorView;
  private autoLinkWrapPlugin = new Plugin({
    appendTransaction: (transactions, oldState, newState) => {
      const { doc: previousDoc } = oldState;
      const toCurrentDoc = (pos: number): number =>
        transactions.reduce((acc, tr) => tr.mapping.map(acc), pos);
      let tr = newState.tr;
      const changes: StepChanges[] = transactions.flatMap(tr => this.getTransactionStepChanges(tr));
      if (!changes.length) return;

      changes.forEach(change => {
        const { oldStart, oldEnd } = change;
        if (previousDoc.content.size < oldEnd) return;

        previousDoc.nodesBetween(oldStart, oldEnd, (node, pos) => {
          if (!node.isTextblock) return true;

          node.content.forEach((node, offset) => {
            if (!node.isText) return;

            const linkMark = node.marks.find(mark => LinkMark.is(mark));
            if (!linkMark) return;

            const attrs = <ProsemirrorAttrs<LinkAttributes>>linkMark.attrs;
            if (attrs.href !== this.textToUrl(node.text!)) return;

            const from = pos + offset + 1;
            const to = from + node.text!.length;
            tr = tr.removeMark(toCurrentDoc(from), toCurrentDoc(to), linkMark);
            tr = this.createLinksFromMatches(tr, toCurrentDoc(from), toCurrentDoc(to));
          });

          return false;
        });

        tr = this.createLinksFromMatches(tr, toCurrentDoc(oldStart), toCurrentDoc(oldEnd));
      });

      const to = tr.doc.resolve(toCurrentDoc(oldState.selection.to));
      const selection = TextSelection.findFrom(to, -1, true);
      return selection ? tr.setSelection(selection) : tr;
    },
  });

  constructor(private config?: { linkMatchers: LinkMatcher[] }) {}

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins() {
    return [this.autoLinkWrapPlugin];
  }

  private matchUrlRegex(text: string): Array<{ phrase: string; index: number }> {
    let result: Array<{ phrase: string; index: number }> = [];
    let match: RegExpMatchArray | null;
    if (!text) return [];

    while ((match = LinkWrapperRegExps.EMAIL_OR_URL_REGEXP_WITH_PARAMS.exec(text)) !== null) {
      result = [
        ...result,
        {
          phrase: match[0].trimEnd(),
          index: match.index!,
        },
      ];
    }

    return result;
  }

  private createLinksFromMatches(tr: Transaction, from: number, to: number) {
    const { schema } = this.editorView.state;
    let nextTr = tr;

    nextTr.doc.nodesBetween(from, to, (node, pos) => {
      if (!node.isTextblock) return true;

      const nodes = this.getTextNodesFromTextblock(node);
      nodes.forEach(node => {
        const result = this.matchUrlRegex(node.text);
        result.forEach(({ index, phrase }) => {
          const from = pos + index + node.pos;
          const to = from + phrase.length;
          nextTr = nextTr.replaceWith(from, to, this.anchorNodeMarkFactory(schema, phrase));
        });
      });
      return false;
    });
    return nextTr;
  }

  private getTextNodesFromTextblock(node: ProsemirrorNode): TextWithPosition[] {
    const { content } = node;
    let offset = 1;
    let lastTextNode: TextWithPosition | null = null;
    let nodes: Array<TextWithPosition | null> = [];

    for (let i = 0; i < content.childCount; i++) {
      const innerNode = content.child(i);
      if (innerNode.isText) {
        lastTextNode = lastTextNode
          ? { pos: lastTextNode.pos, text: lastTextNode.text + innerNode.text! }
          : { text: innerNode.text!, pos: offset };
      } else {
        nodes = [...nodes, lastTextNode];
        lastTextNode = null;
      }
      offset += innerNode.nodeSize;
    }
    return <TextWithPosition[]>[...nodes, lastTextNode].filter(Boolean);
  }

  private getTransactionStepChanges(transaction: Transaction): StepChanges[] {
    let changes: StepChanges[] = [];
    if (!transaction.docChanged) return [];

    transaction.steps.forEach(step =>
      step.getMap().forEach((oldStart, oldEnd, newStart, newEnd) => {
        changes = [
          ...changes,
          {
            oldStart,
            oldEnd,
            newStart,
            newEnd,
          },
        ];
      }),
    );
    return changes;
  }

  private testTextProtocol(text: string): boolean {
    return /^([a-zA-Z0-9]*?):\/\//.test(text);
  }

  private testTextEmailProtocol(text: string): boolean {
    return /^(mailto):/.test(text);
  }

  private testTextEmail(text: string): boolean {
    return LinkWrapperRegExps.EMAIL_REGEXP.test(text);
  }

  private textToUrl(text: string): string {
    const hasProtocol = this.testTextProtocol(text);
    const isEmailType = this.testTextEmail(text);
    const hasEmailProtocol = this.testTextEmailProtocol(text);
    if (isEmailType) return hasEmailProtocol ? text : `mailto:${text}`;

    return hasProtocol ? text : `http://${text}`;
  }

  private anchorNodeMarkFactory(linkSchema: LinkSchema, text: string): ProsemirrorNode {
    const linkMatcher = this.config?.linkMatchers.find(({ regexp }) => regexp.test(text));

    if (linkMatcher) {
      return linkMatcher.linkFunction(linkSchema, text);
    } else {
      const linkAttrs = <LinkAttributes>{
        href: this.textToUrl(text),
        target: '_blank',
        rel: 'noopener',
      };

      return linkSchema.text(text, [linkSchema.marks.link.create(linkAttrs)]);
    }
  }
}
